import { countryCodesDict } from "CONSTANTS";
import { UUID } from "api/types";
import { useLoadingWorkspaceInventory } from "api/wms/hooks";
import { LoadingGroupPackage, LoadingPackageStatus } from "api/wms/models";
import { Button } from "components/miloDesignSystem/atoms/button";
import { Modal } from "components/miloDesignSystem/atoms/modal";
import { Typography } from "components/miloDesignSystem/atoms/typography";
import { Table } from "components/miloDesignSystem/molecules/table";
import { comfortableListUiSchema } from "components/miloDesignSystem/molecules/table/uiSchemas";
import { CommonError } from "components/utils";
import { useSelector } from "hooks";
import { assertIsDefined } from "utilities/assertIsDefined";
import styles from "pages/wms/shared/Wms.module.css";
import { Avatar } from "components/miloDesignSystem/atoms/avatar";
import { useLoadingWorkspaceColumns } from "./useLoadingWorkspaceColumns";
import { WorkspaceInventoryHeader } from "pages/wms/shared/components/WorkspaceInventoryHeader";
import { cx } from "utilities";
import { normalizeUserDetails } from "pages/wms/shared/utils/normalizeUserDetails";

export interface LoadingWorkspaceState {
  user: number | string;
  loading: UUID;
}

interface Props {
  close: () => void;
  state: LoadingWorkspaceState;
}

export const LoadingWorkspaceModal = ({ close, state }: Props) => {
  const { data: loadingInventory, error, isLoading, isFetching } = useLoadingWorkspaceInventory(
    `${state.loading}$${state.user}`,
  );
  const columns = useLoadingWorkspaceColumns();
  const users = useSelector(store => store.partials.users);
  const user = users.find(_user => _user.id === state.user);

  const normalizedUser = normalizeUserDetails(user);

  if (isLoading) return null;

  if (error)
    return (
      <Modal
        close={close}
        isOpen
        width={750}
        title={
          <Typography fontSize="20" fontWeight="700">
            Obszar roboczy
          </Typography>
        }
      >
        <div className="mt-3 py-5">
          <CommonError status={error._httpStatus_} />
        </div>
      </Modal>
    );

  assertIsDefined(loadingInventory);

  return (
    <Modal
      close={close}
      isOpen
      width={990}
      title={
        <Typography fontSize="20" fontWeight="700">
          Obszar roboczy
        </Typography>
      }
    >
      <div className={styles.lineItemContainer}>
        <div className="d-flex align-items-center gap-2 pb-1">
          <Avatar size="sm" theme="light" user={normalizedUser} />
          {normalizedUser && (
            <div className="d-flex align-items-center gap-1">
              {normalizedUser.firstName} {normalizedUser.lastName}
            </div>
          )}
        </div>
        <WorkspaceInventoryHeader
          signature={loadingInventory.loading.signature}
          ramp={loadingInventory.loading.ramp}
          source="Załadunek"
          status={loadingInventory.loading.status}
          joinedAt={loadingInventory.user.joinedAt}
          totalProgress={loadingInventory.loading.totalProgress}
          participation={loadingInventory.user.participation}
        />
        <div className={styles.tableArea}>
          {loadingInventory.loadingGroups.map(loadingGroup => (
            <div>
              <div className={styles.orderHeader} key={loadingGroup.id}>
                <div className={styles.orderHeaderGeneralInfo}>
                  <div className={styles.orderHeaderSignature}>
                    <Typography fontSize="16" fontWeight="700" noWrap>
                      {loadingGroup.signature}
                    </Typography>
                  </div>
                  <div>
                    <Typography color="neutralBlack48" fontSize="12" fontWeight="700">
                      {loadingGroup.position}.
                    </Typography>
                  </div>
                  <div>
                    {loadingGroup.address.countryCode && (
                      <div className="d-flex align-items-center gap-1">
                        <img alt="kraj" src={countryCodesDict[loadingGroup.address.countryCode]} />
                        <Typography fontSize="10" fontWeight="700">
                          {loadingGroup.address.countryCode}
                        </Typography>
                      </div>
                    )}
                  </div>
                </div>
                <div className={styles.orderHeaderCustomer}>
                  <Typography fontSize="12" fontWeight="700" noWrap>
                    {loadingGroup.seller ? loadingGroup.seller.name : "---"}
                  </Typography>
                </div>
                <div className={styles.orderHeaderAddress}>
                  <Typography fontSize="12" fontWeight="400">
                    {loadingGroup.address.postCode} {loadingGroup.address.city}
                  </Typography>
                </div>
              </div>
              <div className={styles.tableContainer}>
                {Boolean(loadingGroup.packages.length) && (
                  <Table<LoadingGroupPackage>
                    rows={loadingGroup.packages}
                    columns={columns}
                    isLoading={isLoading || isFetching}
                    error={error}
                    uiSchema={comfortableListUiSchema}
                    overrides={row => {
                      return {
                        row: {
                          className: cx({
                            [styles.finishedItem]: row.status === LoadingPackageStatus.LOADED,
                            [styles.inProgressItem]:
                              row.status === LoadingPackageStatus.DURING_LOADING ||
                              row.status === LoadingPackageStatus.PICKED_UP,
                          }),
                        },
                      };
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.closeBtn}>
          <Button className="text-uppercase" onClick={close} size="medium" variant="transparent">
            Zamknij
          </Button>
        </div>
      </div>
    </Modal>
  );
};
